import { useState, useEffect } from 'react'
import {
  Building2,
  CreditCard,
  Settings2,
  DollarSign,
  Save,
  Loader2,
  Eye,
  EyeOff,
  AlertTriangle
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import {
  getCasinoSettings,
  updateCasinoSettings,
  getDefaultSettings,
  formatCurrency
} from '@/features/admin/services/adminService'
import type { CasinoSettings } from '@/types'

export default function AdminConfig() {
  const { user } = useAuth()
  const [settings, setSettings] = useState<CasinoSettings>(getDefaultSettings())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showToken, setShowToken] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    setLoading(true)
    const data = await getCasinoSettings()
    setSettings(data)
    setLoading(false)
  }

  const handleSave = async () => {
    if (!user) return

    setSaving(true)
    setSaveMessage(null)

    const result = await updateCasinoSettings(settings, user.uid)

    if (result.success) {
      setSaveMessage({ type: 'success', text: 'Configuración guardada correctamente' })
      setHasChanges(false)
    } else {
      setSaveMessage({ type: 'error', text: result.error || 'Error al guardar' })
    }

    setSaving(false)

    // Clear message after 3 seconds
    setTimeout(() => setSaveMessage(null), 3000)
  }

  const updateField = (
    section: 'bankAccount' | 'mercadoPagoAccount' | 'mercadoPagoApi' | 'limits' | 'general',
    field: string,
    value: string | number | boolean
  ) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...(prev[section] as Record<string, unknown>),
        [field]: value,
      },
    }))
    setHasChanges(true)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-casino-gold" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-cinzel text-2xl text-casino-gold">Configuración del Casino</h1>
        <Button
          variant="gold"
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Guardar Cambios
        </Button>
      </div>

      {saveMessage && (
        <div
          className={`p-4 rounded-lg ${
            saveMessage.type === 'success'
              ? 'bg-green-500/20 border border-green-500/50 text-green-400'
              : 'bg-red-500/20 border border-red-500/50 text-red-400'
          }`}
        >
          {saveMessage.text}
        </div>
      )}

      <Tabs defaultValue="accounts" className="space-y-6">
        <TabsList className="bg-zinc-800/50">
          <TabsTrigger value="accounts" className="data-[state=active]:bg-casino-gold/20">
            <Building2 className="w-4 h-4 mr-2" />
            Cuentas
          </TabsTrigger>
          <TabsTrigger value="api" className="data-[state=active]:bg-casino-gold/20">
            <CreditCard className="w-4 h-4 mr-2" />
            MercadoPago API
          </TabsTrigger>
          <TabsTrigger value="limits" className="data-[state=active]:bg-casino-gold/20">
            <DollarSign className="w-4 h-4 mr-2" />
            Límites
          </TabsTrigger>
          <TabsTrigger value="general" className="data-[state=active]:bg-casino-gold/20">
            <Settings2 className="w-4 h-4 mr-2" />
            General
          </TabsTrigger>
        </TabsList>

        {/* Cuentas del Casino */}
        <TabsContent value="accounts" className="space-y-6">
          {/* Cuenta Bancaria */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-casino-gold" />
                Cuenta Bancaria
              </CardTitle>
              <CardDescription>
                Datos de la cuenta bancaria para recibir transferencias
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="bank-cbu">CBU (22 dígitos)</Label>
                <Input
                  id="bank-cbu"
                  value={settings.bankAccount.cbu}
                  onChange={(e) => updateField('bankAccount', 'cbu', e.target.value)}
                  placeholder="0000000000000000000000"
                  maxLength={22}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bank-alias">Alias</Label>
                <Input
                  id="bank-alias"
                  value={settings.bankAccount.alias}
                  onChange={(e) => updateField('bankAccount', 'alias', e.target.value)}
                  placeholder="MI.ALIAS.BANCO"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bank-banco">Banco</Label>
                <Input
                  id="bank-banco"
                  value={settings.bankAccount.banco}
                  onChange={(e) => updateField('bankAccount', 'banco', e.target.value)}
                  placeholder="Banco Macro"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bank-titular">Titular</Label>
                <Input
                  id="bank-titular"
                  value={settings.bankAccount.titular}
                  onChange={(e) => updateField('bankAccount', 'titular', e.target.value)}
                  placeholder="NOMBRE APELLIDO"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="bank-cuit">CUIT/CUIL</Label>
                <Input
                  id="bank-cuit"
                  value={settings.bankAccount.cuit}
                  onChange={(e) => updateField('bankAccount', 'cuit', e.target.value)}
                  placeholder="20-12345678-9"
                />
              </div>
            </CardContent>
          </Card>

          {/* Cuenta MercadoPago */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-400" />
                Cuenta MercadoPago
              </CardTitle>
              <CardDescription>
                Datos de la cuenta de MercadoPago para recibir pagos manuales
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="mp-cvu">CVU (22 dígitos)</Label>
                <Input
                  id="mp-cvu"
                  value={settings.mercadoPagoAccount.cvu}
                  onChange={(e) => updateField('mercadoPagoAccount', 'cvu', e.target.value)}
                  placeholder="0000000000000000000000"
                  maxLength={22}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mp-alias">Alias</Label>
                <Input
                  id="mp-alias"
                  value={settings.mercadoPagoAccount.alias}
                  onChange={(e) => updateField('mercadoPagoAccount', 'alias', e.target.value)}
                  placeholder="mi.alias.mp"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="mp-titular">Titular</Label>
                <Input
                  id="mp-titular"
                  value={settings.mercadoPagoAccount.titular}
                  onChange={(e) => updateField('mercadoPagoAccount', 'titular', e.target.value)}
                  placeholder="NOMBRE APELLIDO"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* MercadoPago API */}
        <TabsContent value="api" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-400" />
                Integración MercadoPago API
              </CardTitle>
              <CardDescription>
                Credenciales para pagos automáticos con MercadoPago
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
                <div className="space-y-1">
                  <Label>Pagos Automáticos</Label>
                  <p className="text-sm text-gray-400">
                    Habilitar acreditación automática de fichas
                  </p>
                </div>
                <Switch
                  checked={settings.mercadoPagoApi.enabled}
                  onCheckedChange={(checked) => updateField('mercadoPagoApi', 'enabled', checked)}
                />
              </div>

              {!settings.mercadoPagoApi.enabled && (
                <div className="flex items-center gap-2 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-400">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="text-sm">
                    Los pagos automáticos están deshabilitados. Los depósitos requerirán aprobación manual.
                  </span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="mp-public-key">Public Key</Label>
                <Input
                  id="mp-public-key"
                  value={settings.mercadoPagoApi.publicKey}
                  onChange={(e) => updateField('mercadoPagoApi', 'publicKey', e.target.value)}
                  placeholder="APP_USR-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mp-access-token">Access Token (Producción)</Label>
                <div className="relative">
                  <Input
                    id="mp-access-token"
                    type={showToken ? 'text' : 'password'}
                    value={settings.mercadoPagoApi.accessToken}
                    onChange={(e) => updateField('mercadoPagoApi', 'accessToken', e.target.value)}
                    placeholder="APP_USR-xxxxxxxxxxxx-xxxxxx-xxxxxxxx..."
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowToken(!showToken)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  Obtené las credenciales en{' '}
                  <a
                    href="https://www.mercadopago.com.ar/developers/panel/app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-casino-gold hover:underline"
                  >
                    MercadoPago Developers
                  </a>
                </p>
              </div>

              <div className="p-4 bg-zinc-800/50 rounded-lg space-y-2">
                <Label className="text-sm font-medium">Webhook URL</Label>
                <code className="block p-2 bg-black/50 rounded text-sm text-casino-gold break-all">
                  https://cosanostracasino.online/api/mercadopago-webhook
                </code>
                <p className="text-xs text-gray-500">
                  Configurá esta URL en el panel de MercadoPago Developers → Webhooks
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Límites de Transacciones */}
        <TabsContent value="limits" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-400" />
                Límites de Depósitos
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="min-deposit">Depósito Mínimo</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <Input
                    id="min-deposit"
                    type="number"
                    value={settings.limits.minDeposit}
                    onChange={(e) => updateField('limits', 'minDeposit', parseInt(e.target.value) || 0)}
                    className="pl-7"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Actual: {formatCurrency(settings.limits.minDeposit)}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="max-deposit">Depósito Máximo</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <Input
                    id="max-deposit"
                    type="number"
                    value={settings.limits.maxDeposit}
                    onChange={(e) => updateField('limits', 'maxDeposit', parseInt(e.target.value) || 0)}
                    className="pl-7"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Actual: {formatCurrency(settings.limits.maxDeposit)}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-red-400" />
                Límites de Retiros
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="min-withdrawal">Retiro Mínimo</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <Input
                    id="min-withdrawal"
                    type="number"
                    value={settings.limits.minWithdrawal}
                    onChange={(e) => updateField('limits', 'minWithdrawal', parseInt(e.target.value) || 0)}
                    className="pl-7"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Actual: {formatCurrency(settings.limits.minWithdrawal)}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="max-withdrawal">Retiro Máximo Diario</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <Input
                    id="max-withdrawal"
                    type="number"
                    value={settings.limits.maxDailyWithdrawal}
                    onChange={(e) => updateField('limits', 'maxDailyWithdrawal', parseInt(e.target.value) || 0)}
                    className="pl-7"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Actual: {formatCurrency(settings.limits.maxDailyWithdrawal)}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuración General */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings2 className="w-5 h-5 text-casino-gold" />
                Modo Demo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="demo-balance">Balance Inicial Demo</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <Input
                    id="demo-balance"
                    type="number"
                    value={settings.general.demoBalance}
                    onChange={(e) => updateField('general', 'demoBalance', parseInt(e.target.value) || 0)}
                    className="pl-7"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Fichas que reciben los usuarios nuevos en modo demo
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-casino-gold" />
                Apuestas Mínimas por Juego
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="min-ruleta">Ruleta</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <Input
                    id="min-ruleta"
                    type="number"
                    value={settings.general.minBetRuleta}
                    onChange={(e) => updateField('general', 'minBetRuleta', parseInt(e.target.value) || 0)}
                    className="pl-7"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="min-blackjack">Blackjack</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <Input
                    id="min-blackjack"
                    type="number"
                    value={settings.general.minBetBlackjack}
                    onChange={(e) => updateField('general', 'minBetBlackjack', parseInt(e.target.value) || 0)}
                    className="pl-7"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="min-baccarat">Baccarat</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <Input
                    id="min-baccarat"
                    type="number"
                    value={settings.general.minBetBaccarat}
                    onChange={(e) => updateField('general', 'minBetBaccarat', parseInt(e.target.value) || 0)}
                    className="pl-7"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-400">
                <AlertTriangle className="w-5 h-5" />
                Modo Mantenimiento
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="space-y-1">
                  <Label>Activar Modo Mantenimiento</Label>
                  <p className="text-sm text-gray-400">
                    Deshabilita el acceso a los juegos para todos los usuarios
                  </p>
                </div>
                <Switch
                  checked={settings.general.maintenanceMode}
                  onCheckedChange={(checked) => updateField('general', 'maintenanceMode', checked)}
                />
              </div>
            </CardContent>
          </Card>

          {settings.updatedAt && (
            <p className="text-sm text-gray-500 text-center">
              Última actualización: {settings.updatedAt.toLocaleString('es-AR')}
            </p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
